import posthog from 'posthog-js';

// Exactly one init path may run (the inline index.html snippet is removed).
let _posthogInitialized = false;

// Initialize PostHog
export const initPostHog = () => {
  if (_posthogInitialized) {
    console.log("[PostHog] Already initialized, skipping duplicate init");
    return;
  }
  const apiKey = process.env.REACT_APP_POSTHOG_KEY;
  const apiHost = process.env.REACT_APP_POSTHOG_HOST;

  if (!apiKey) {
    console.log("[PostHog] No API key configured, skipping initialization");
    return;
  }

  posthog.init(apiKey, {
    api_host: apiHost || 'https://us.i.posthog.com',
    // Privacy: only build person profiles for identified users (preserved from
    // the retired inline init).
    person_profiles: 'identified_only',
    // Privacy: never record sessions (preserved).
    disable_session_recording: true,
    // Privacy: no performance capture (preserved).
    capture_performance: false,
    // Keep payloads minimal — no autocapture (stricter than the old snippet,
    // which only disabled copied-text capture).
    autocapture: false,
    // Capture pageviews manually for SPAs
    capture_pageview: false,
    // Don't capture page leave events automatically
    capture_pageleave: false,
    // Persistence for user identification
    persistence: 'localStorage',
    // Minimal data collection
    property_denylist: ['$ip'],
  });

  _posthogInitialized = true;
  console.log("[PostHog] Initialized successfully");
};

// Identify user consistently
export const identifyUser = (userId) => {
  if (!userId) {
    posthog.reset();
    return;
  }
  
  posthog.identify(userId);
};

// Clear user on logout
export const resetUser = () => {
  posthog.reset();
};

// ============== EVENT TRACKING ==============
// All events use minimal payloads as requested

// App lifecycle
export const trackAppOpen = () => {
  posthog.capture('app_open');
};

// Auth events
export const trackSignupComplete = () => {
  posthog.capture('signup_complete');
};

// Onboarding events
export const trackOnboardingStart = () => {
  posthog.capture('onboarding_start');
};

export const trackOnboardingComplete = () => {
  posthog.capture('onboarding_complete');
};

// Plan generation events
export const trackPlanGenerateStart = (numDays) => {
  posthog.capture('plan_generate_start', {
    num_days: numDays,
  });
};

export const trackPlanGenerateSuccess = (numDays, numMeals) => {
  posthog.capture('plan_generate_success', {
    num_days: numDays,
    num_meals: numMeals,
  });
};

export const trackPlanGenerateFail = (errorType) => {
  posthog.capture('plan_generate_fail', {
    error_type: errorType,
  });
};

// Grocery events
export const trackGroceryOpen = () => {
  posthog.capture('grocery_open');
};

// Food logging events
export const trackLogFoodAdd = (mealType) => {
  posthog.capture('log_food_add', {
    meal_type: mealType,
  });
};

export const trackLogFoodRemove = (mealType) => {
  posthog.capture('log_food_remove', {
    meal_type: mealType,
  });
};

// Meal plan events
export const trackMealAddToDay = (mealType) => {
  posthog.capture('meal_add_to_day', {
    meal_type: mealType,
  });
};

export const trackMealRemoveFromDay = (mealType) => {
  posthog.capture('meal_remove_from_day', {
    meal_type: mealType,
  });
};

// Weekly plan events
export const trackRegenerateWeek = () => {
  posthog.capture('regenerate_week');
};

// Generic page view tracking for SPAs
export const trackPageView = (pageName) => {
  posthog.capture('$pageview', {
    page_name: pageName,
  });
};

export default posthog;
